import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useGroup } from '../contexts/GroupContext';
import { useNotifications } from '../contexts/NotificationContext';
import { destinationOf } from '../nucleus/notifySpace';

import {
    ShellProvider, Shell, QuickHelp,
} from '@ofself/shell';
import { TileColumn } from '@ofself/shell';
import { createSpace, putAwayChrome } from '../pages/nucleus/ShellChrome';
import { GLYPHS } from './nucleus/glyphs';
import { ConfirmProvider } from './nucleus/actions';
import { sectionsFor, pagesFor, placeOf, pathFor, aboutOf } from '../nucleus/map';
import { useSpaceRole } from '../nucleus/access';
import { RecoveryCodesNotice } from '../pages/self/RecoveryCodes';
import GlobalSearch from './nucleus/GlobalSearch';
import ProfileGate from './nucleus/ProfileGate';
import { askSearch } from '../nucleus/find';
import { PhoneBar, useIsPhone } from './nucleus/PhoneNav';
import { applyPageLines } from '../nucleus/pageLines';

// the page lines' setting, on the document before the first paint
applyPageLines();
import { useAttention } from '../nucleus/attention';

import { IconSelf } from './icons/NavIcons';
import { EVENTS } from '../shared';
import { getAppPreview } from '../utils/appPreview';
import api from '../services/api';
import '../styles/Layout.css';
import NucleusMark from './nucleus/NucleusMark';
import { helpPath } from '../nucleus/concepts';
import { ago } from '../nucleus/format';


// (HELP_LINKS retired 2026-08-23: the help seat opens Quick Help directly,
// and its foot carries the one real door — support@ofself.com.)

// THE RAIL IS THE MAP (nucleus/map.js): one door per section, in the order the
// app reads — Self, Graph, Realms, then who you let in, then Access and
// Activity. Each door opens its section in the space you are in.

function Layout({ children }) {
    const { user, logout, encKeys } = useAuth();
    const { groups, activeGroup, activeGroupId, switchToPersonal, refreshGroups } = useGroup();
    const { role: spaceRole } = useSpaceRole();
    const { notifications, unreadCount, markRead, markAllRead, counts, hasMore, loadMore, attentionStamp } = useNotifications();
    const navigate = useNavigate();
    const location = useLocation();

    const [quickHelp, setQuickHelp] = useState(false);
    const openHelpPage = useCallback(() => { putAwayChrome(); navigate(helpPath(activeGroupId)); }, [navigate, activeGroupId]);
    useEffect(() => {
        const onKey = (e) => {
            if (!(e.metaKey || e.ctrlKey) || e.altKey || e.repeat) return;
            if (e.key !== '?' && !(e.shiftKey && e.key === '/')) return;
            e.preventDefault();
            setQuickHelp((v) => !v);
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, []);

    // THE SETTINGS WINDOW, AS THE MAC SEATS IT (Views.swift: General, Account,
    // Billing; then Help): Self (the Mac's Account), Billing — what you pay
    // for, receipts, the refund, and the way to your authorized apps — and Help.

    const [personalAvatarUrl, setPersonalAvatarUrl] = useState(null);
    const refreshAvatar = useCallback(async () => {
        if (!user) { setPersonalAvatarUrl(null); return; }
        try {
            const res = await api.getNodes({ schema_id: 'profile', limit: 1 });
            const node = (res?.nodes || res || [])[0];
            const fileId = node?.value_json?.avatar_file_id;
            if (!fileId) { setPersonalAvatarUrl(null); return; }
            const dl = await api.getNodeDownloadUrl(fileId);
            setPersonalAvatarUrl(dl?.download_url || null);
        } catch { /* no avatar set */ }
    }, [user]);
    useEffect(() => { refreshAvatar(); }, [refreshAvatar]);
    useEffect(() => {
        window.addEventListener(EVENTS.PROFILE_AVATAR_CHANGED, refreshAvatar);
        return () => window.removeEventListener(EVENTS.PROFILE_AVATAR_CHANGED, refreshAvatar);
    }, [refreshAvatar]);

    // An app-catalog preview tab is a different entity: Graph is its only door.
    const preview = getAppPreview();
    const groupId = activeGroup?.id || null;
    const { section, page } = preview ? { section: null, page: null } : placeOf(location.pathname, groupId);
    // SEARCH EVERYTHING lives on Home: ⌘K / Ctrl+K on any other page goes
    // there and puts you in it (on Home the search takes ⌘K itself)
    const awayFromHome = !!section || !!preview;
    useEffect(() => {
        if (!awayFromHome) return undefined;
        const onKey = (e) => {
            if (!(e.metaKey || e.ctrlKey) || e.altKey || e.key.toLowerCase() !== 'k') return;
            e.preventDefault();
            askSearch();
            navigate('/home');
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [awayFromHome, navigate]);
    // a page only a group's admins reach is listed for them alone
    const pages = section ? pagesFor(section, groupId, { admin: !groupId || spaceRole === 'admin' }) : [];
    const doors = preview
        ? [{ id: 'graph', label: 'Graph', glyph: 'graph', zone: 'internal', go: () => navigate(`/app/${preview.id}/graph`) }]
        : sectionsFor(groupId).map((sec) => ({ ...sec, go: () => navigate(pathFor(sec.id, null, groupId)) }));
    // the rail, top to bottom: the external layer leads with who (People,
    // Groups), then what (Apps, Plugins); the rest reads the map from the
    // bottom up. A section the list doesn't name keeps its reversed place.
    // the rail, top to bottom: who you let in, the gates, then you
    const RAIL = ['apps', 'people', 'groups', 'plugins', 'realms', 'graph', 'activity', 'self'];
    const railOrder = (list) => [
        ...RAIL.map((id) => list.find((d) => d.id === id)).filter(Boolean),
        ...list.filter((d) => !RAIL.includes(d.id)),
    ];
    // On a phone the page stands alone: the bottom bar moves between sections
    // and the page's strip between its pages (PhoneNav.js), so the column's
    // own back-and-forth track is never used.
    const phone = useIsPhone();
    // the page's verbs sit in the shell's corner, outside the desk: the zone
    // rides the document too, so they wear the section's layer (Layout.css)
    const zone = section?.zone || '';
    const tone = section?.tone || '';
    useEffect(() => {
        const root = document.documentElement;
        if (zone) root.dataset.nxZone = zone; else delete root.dataset.nxZone;
        if (tone) root.dataset.nxTone = tone; else delete root.dataset.nxTone;
        return () => { delete root.dataset.nxZone; delete root.dataset.nxTone; };
    }, [zone, tone]);
    // WHAT WANTS YOU rides the tile it lives on (and Home): requests,
    // invitations, proposals waiting, the inbox's unread.
    const attention = useAttention(groupId, { inbox: unreadCount, section: section?.id || 'home', off: !!preview, stamp: attentionStamp });
    const pageCount = (id) => attention[`${section?.id}/${id}`] || undefined;



    // A group's photo is `avatar_url` on the API and `avatarUrl` to the shell.
    const spaceOf = (g) => ({ ...g, avatarUrl: g.avatar_url || undefined });

    // A group with something waiting shows on the switcher, so you can see it
    // without leaving the space you are in. The plate itself only ever shows the
    // space you are standing in.
    const otherSpaceUnreads = Object.entries(counts?.groups || {})
        .filter(([id]) => id !== activeGroup?.id)
        .reduce((sum, [, n]) => sum + (n || 0), 0)
        + (activeGroup ? (counts?.personal || 0) : 0);

    // The shell's only sanctioned "what space am I in" field is activeGroup
    // (@ofself/shell AGENT.md) — an app preview borrows it rather than
    // inventing new shell-facing chrome. Deliberately NOT added to `groups`:
    // that array is the switcher's picker of spaces you can switch INTO, and
    // a preview is somewhere you arrive via the dev-portal handoff, never a
    // destination to switch to from elsewhere.
    const appPreview = getAppPreview();

    // AN APP PREVIEW IS ITS OWN ENTITY (appPreview.js): the dock shows no
    // Personal space (the kit lists it whenever currentUser is known), no
    // inbox, no unread counts and no way to make or switch spaces — only the
    // preview itself and the ways out.
    const ecoValue = appPreview ? {
        currentUser: null,
        groups: [],
        activeGroup: { id: appPreview.id, name: `${appPreview.name || 'App'} — App preview` },
        spaceUnreads: {},
        hasOtherSpaceUnreads: false,
        appUnreads: {},
        onSignOut: async () => {
            try { await logout({ scope: 'app' }); } catch (e) { /* local state is cleared regardless */ }
            window.location.href = '/login';
        },
    } : {
        currentUser: user ? { username: user.username, email: user.email, avatarUrl: personalAvatarUrl } : null,
        spaceUnreads: { home: counts?.personal || 0, ...(counts?.groups || {}) },
        hasOtherSpaceUnreads: otherSpaceUnreads > 0,
        appUnreads: { nucleus: unreadCount + otherSpaceUnreads },
        // THE DOCK'S HELP: the Help page, every concept on one page (pages/help).
        // Quick Help, the hover card, stays on ⌘? and on that page.
        onHelp: openHelpPage,
        // THE DOCK'S NEW SPACE (Dock.swift onNewSpace): a group, made the way
        // People › New group makes one (ShellChrome.createSpace), and ending where
        // that does: on what you share with it.
        onCreateSpace: async ({ name, description, image }) => {
            const { group } = await createSpace({ name, description, image }, { encKeys, refreshGroups }) || {};
            if (group?.id) navigate(`/grant?to_group_id=${encodeURIComponent(group.id)}&made=1`);
        },
        notificationInbox: {
            unreadCount, hasMore, onLoadMore: loadMore,
            items: (notifications || []).map(n => ({
                id: n.id, title: n.title, message: n.message,
                time: ago(n.created_at, { bare: true }), read: !!n.is_read,
                // a row about a group opens in that group, on the item, the
                // way the Activity inbox opens it (nucleus/notifySpace.js)
                to: destinationOf(n, activeGroupId || 'personal'),
            })),
            onItemPress: async item => {
                if (!item.read) await markRead(item.id);
                if (!item.to) return;
                if (item.to.mustSwitchSpace && item.to.space === 'personal') switchToPersonal();
                navigate(item.to.url);
            },
            onMarkAllRead: markAllRead,
        },
        groups: (groups || []).map(spaceOf),
        activeGroup: activeGroup ? spaceOf(activeGroup) : null,
        onSwitchSpace: (space) => {
            if (space && space.id) {
                navigate(`/group/${space.id}/graph`);
            } else {
                switchToPersonal();
                navigate('/graph');
            }
        },
        // Connect an unauthorized group: send the user through the group
        // authorization flow (pick what to share with the group), then return.
        onConnectSpace: (space) => {
            if (!space?.id) return;
            navigate(`/grant?to_group_id=${space.id}&redirect_uri=${encodeURIComponent('/people')}`);
        },
        fetchAuthorizedApps: () => api.request(
            activeGroup?.id
                ? `/app-shell/apps?group_id=${encodeURIComponent(activeGroup.id)}`
                : '/app-shell/apps'
        ),
        // TWO DOORS, TWO SCOPES. The console shows the ecosystem cell only
        // when `onSignOutEcosystem` is supplied — ending every session across
        // the fleet is a different act from leaving one app, and one button
        // silently picking a scope is how someone gets signed out of
        // everything when they meant to close a tab.
        onSignOut: async () => {
            try { await logout({ scope: 'app' }); } catch (e) { /* local state is cleared regardless */ }
            window.location.href = '/login';
        },
        onSignOutEcosystem: async () => {
            try { await logout({ scope: 'global' }); } catch (e) { /* local state is cleared regardless */ }
            window.location.href = '/login';
        },
    };

    // a door as the kit's rail reads a seat
    const railSeat = (door) => {
        const Glyph = GLYPHS[door.glyph];
        return {
            id: door.id,
            label: door.label,
            glyph: <span className="nx-rail-glyph" data-zone={door.zone} data-tone={door.tone}><Glyph size={15} strokeWidth={1.5} /><span className="nx-rail-word">{door.label}</span></span>,
            active: preview ? true : section?.id === door.id,
            onClick: door.go,
            // the inbox lives in Activity: its unread count rides that door
            badge: door.id === 'activity' && unreadCount > 0 ? (unreadCount > 99 ? '99' : unreadCount) : undefined,
        };
    };

    const desk = (
        <div className="block block--fill nucleus-main ofself-desk" data-zone={section?.zone || undefined} data-tone={section?.tone || undefined}>
            {/* search everything lives on Home only: the front door is where you look
                across the space; ⌘K anywhere comes back here to it */}
            {!preview && !section && <GlobalSearch groupId={groupId} onHelp={openHelpPage} />}
            {children}
        </div>
    );

    return (
        <ShellProvider value={ecoValue}>
            <ConfirmProvider>
            {/* THE APP RAIL + THE DOCK: the kit seats the rail — nucleus's mark
                (home), the map's sections in reading order, then Preferences,
                Help and the avatar, whose dock holds the spaces, the apps and
                the inbox. */}
            <Shell
                currentApp="nucleus"
                // the Nucleus mark: the double lotus (components/nucleus/marks/nucleus.js)
                mark={<NucleusMark size={34} title="Nucleus" />}
                rail
                dock
                onHome={() => navigate('/home')}
                activeSeat={section?.id || (preview ? 'graph' : /\/help$/.test(location.pathname) ? 'help' : 'home')}
                // inside out FROM THE BOTTOM: the avatar (who you are, and the
                // switch between your identities and spaces) stands at the
                // rail's foot, so Self sits just above it and the rail builds
                // outward to the top — internal, then gates, then external
                // Help stands last, under the sections; it is in the dock's foot too (⌘O)
                railItems={['logo', ...railOrder(doors).map(railSeat)]}
                // the dock's own page is the platform's Appearance; Help is the dock's footer
                settings={[]}
            >
                {/* THE SECTION COLUMN: the section's pages as tiles, the page you
                    are on lit — one column, the same in every section. */}
                {section && pages.length > 1 ? (
                    <TileColumn
                        label={section.label}
                        // a label as an element, so the kit breaks it between words only
                        // (it hyphenates plain strings: "Exposure pro-files"), with the
                        // page's line beneath it in quiet text (hidden by Appearance)
                        items={pages.map((p) => ({
                            id: p.id,
                            label: <span className="nx-tile">{p.label}{aboutOf(p, groupId) && <span className="nx-tile__about">{aboutOf(p, groupId)}</span>}</span>,
                            count: pageCount(p.id),
                        }))}
                        active={page?.id}
                        onSelect={(id) => navigate(pathFor(section.id, id, groupId))}
                        storageKey="nucleus:section-col"
                        className="nucleus-section"
                        data-zone={section.zone}
                        data-tone={section.tone}
                        mobileIndex={1}
                        trailing={desk}
                    />
                ) : desk}
                {phone && !preview && <PhoneBar doors={railOrder(doors)} active={section?.id || null} onHome={() => navigate('/home')} />}
                {!appPreview && <RecoveryCodesNotice />}
                {!appPreview && !preview && <ProfileGate />}
                <QuickHelp open={quickHelp} onClose={() => setQuickHelp(false)} anchor="bottom-left"
                    supportEmail="support@ofself.com" helpHref={helpPath(activeGroupId)}
                    onDocs={() => { setQuickHelp(false); openHelpPage(); }} />
            </Shell>
            </ConfirmProvider>
        </ShellProvider>
    );
}

export default Layout;
